import { modulePermissionMap } from '@/constants/permissions';

/**
 * 遞迴提取所有權限 ID
 * @param {Array} permissions - 權限樹狀結構
 * @returns {Array} 扁平化的權限 ID 清單
 */
export function extractAllPermissionIds(permissions) {
    const ids = [];

    function extract(permissionList) {
        permissionList.forEach((permission) => {
            ids.push(permission.id);
            if (permission.data && permission.data.length > 0) {
                extract(permission.data);
            }
        });
    }

    extract(permissions);
    return ids;
}

/**
 * 根據權限過濾路由
 * @param {Array} permissionList - 權限清單
 * @param {Array} modulesConfig - 模組配置
 * @returns {Array} 過濾後的路由清單
 */
export function filterRoutesByPermissions(permissionList, modulesConfig) {
    // 提取所有權限 ID
    const permissionIds = extractAllPermissionIds(permissionList);

    // 建立模組權限映射
    const modulePermissions = new Map();
    Object.entries(modulePermissionMap).forEach(([modulePath, config]) => {
        modulePermissions.set(modulePath, config);
    });

    // 收集有權限的模組
    const authorizedModules = new Map();

    // 檢查每個模組的權限
    // 當 folder 為 null/undefined 時視為「未設定權限 ID」，仍註冊路由，避免權限表重置後整站空白
    const isFolderPermissionUnset = (folderId) =>
        folderId === null || folderId === undefined;
    const hasAnyRealId = (ids) =>
        Array.isArray(ids) && ids.some((id) => id !== null && id !== undefined);

    modulesConfig.forEach(({ folder, children }) => {
        const modulePath = folder.path;
        const moduleConfig = modulePermissions.get(modulePath);

        if (!moduleConfig) {
            return;
        }

        const folderId = moduleConfig.folder;
        const hasFolderPermission =
            isFolderPermissionUnset(folderId) ||
            permissionIds.includes(folderId);

        if (hasFolderPermission) {
            if (children.length === 0) {
                authorizedModules.set(modulePath, {
                    ...folder,
                    children: []
                });
            } else {
                const authorizedChildren = children
                    .map((child) => {
                        const childPermissions =
                            moduleConfig.children[child.name];
                        if (!childPermissions || !hasAnyRealId(childPermissions)) {
                            return {
                                child: {
                                    ...child,
                                    meta: {
                                        ...child.meta,
                                        permissionIds: []
                                    }
                                },
                                hasPermission: true
                            };
                        }

                        const hasChildPermission = childPermissions.some(
                            (permissionId) =>
                                permissionIds.includes(permissionId)
                        );

                        const routeWithPermissions = {
                            ...child,
                            meta: {
                                ...child.meta,
                                permissionIds: childPermissions
                            }
                        };

                        return {
                            child: routeWithPermissions,
                            hasPermission: hasChildPermission
                        };
                    })
                    .filter((item) => item.hasPermission)
                    .map((item) => item.child);

                if (authorizedChildren.length > 0) {
                    authorizedModules.set(modulePath, {
                        ...folder,
                        children: authorizedChildren
                    });
                }
            }
        }
    });

    // 按照 modulesConfig 順序排列結果
    const result = modulesConfig
        .map(({ folder }) => authorizedModules.get(folder.path))
        .filter(Boolean);

    return result;
}

/**
 * 檢查路由是否有權限
 * @param {Object} route - 路由物件
 * @param {Array} permissionIds - 權限 ID 清單
 * @returns {boolean} 是否有權限
 */
export function hasRoutePermission(route, permissionIds) {
    if (!route.meta || !route.meta.permissionIds) {
        return false;
    }

    return route.meta.permissionIds.some((id) => permissionIds.includes(id));
}

/**
 * 為路由添加權限資訊
 * @param {Object} route - 路由物件
 * @param {Array} permissionIds - 權限 ID 清單
 * @returns {Object} 添加權限資訊後的路由
 */
export function addRoutePermissions(route, permissionIds) {
    const modulePath = route.path;
    const moduleConfig = modulePermissionMap[modulePath];

    if (!moduleConfig) {
        return route;
    }

    // 為子路由添加權限資訊
    const childrenWithPermissions = route.children.map((child) => {
        const childPermissions = moduleConfig.children[child.name];
        if (childPermissions) {
            return {
                ...child,
                meta: {
                    ...child.meta,
                    permissionIds: childPermissions
                }
            };
        }
        return child;
    });

    return {
        ...route,
        children: childrenWithPermissions
    };
}
